export interface SourceTypeHubConfig {
  label: string;
  graphType: string;
  iconColor: string;
}

export const SOURCE_TYPE_HUB_CONFIG: Record<string, SourceTypeHubConfig> = {
  osint: { label: 'OSINT', graphType: 'osint_source', iconColor: '#14b8a6' },
  ai_analysis: { label: 'AI Analysis', graphType: 'ai_analysis', iconColor: '#8b5cf6' },
  leak: { label: 'Leak', graphType: 'leak_source', iconColor: '#ef5350' },
  drill_down: { label: 'Drill-down', graphType: 'phone', iconColor: '#22c55e' },
  derived: { label: 'Derived', graphType: 'social_profile', iconColor: '#64748b' },
};

const DEFAULT_HUB_CONFIG: SourceTypeHubConfig = {
  label: 'Source',
  graphType: 'social_profile',
  iconColor: '#64748b',
};

const HUB_SKIP_SOURCE_TYPES = new Set(['root', 'analyst', 'manual']);

export function resolveNodeSourceType(node: any): string {
  const refs = Array.isArray(node?.sourceReferences) ? node.sourceReferences : [];
  const refType = refs.find((ref: any) => ref?.sourceType)?.sourceType;
  return String(refType || node?.sourceType || 'derived').toLowerCase();
}

export function isSourceTypeHubNode(node: any): boolean {
  return Boolean(
    node?.metadata?.isSourceTypeHub || String(node?.id || '').startsWith('source-hub:'),
  );
}

export function isLayerSourceHeroNode(node: any, rootId?: string): boolean {
  if (!node?.id || node.id === rootId) return false;
  const type = String(node?.type || '').toLowerCase();
  if (!['phone', 'email', 'username'].includes(type)) return false;
  return resolveNodeSourceType(node) === 'root';
}

export function buildSourceTypeHubId(parentId: string, sourceType: string): string {
  return `source-hub:${parentId}:${sourceType}`;
}

export function resolveSourceTypeLabel(sourceType: string): string {
  const normalized = String(sourceType || 'derived').toLowerCase();
  const config = SOURCE_TYPE_HUB_CONFIG[normalized];
  if (config?.label) return config.label;
  return normalized.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());
}

export function resolveNodeDisplayValue(node: any): string {
  return String(node?.label || node?.metadata?.queryValue || node?.id || '').trim();
}

export function buildSourceTypeHubLabel(parentNode: any, sourceType: string): string {
  const value = resolveNodeDisplayValue(parentNode);
  const sourceLabel = resolveSourceTypeLabel(sourceType);
  return value ? `${value} (${sourceLabel})` : sourceLabel;
}

export interface ExpandedGraphPayload {
  nodes: any[];
  edges: any[];
}

function shouldCreateHubForSourceType(sourceType: string): boolean {
  return !HUB_SKIP_SOURCE_TYPES.has(sourceType);
}

function buildDirectedChildrenMap(
  nodes: any[],
  edges: any[],
  rootId: string,
): Map<string, string[]> {
  const nodeIds = new Set(nodes.map((node) => node.id));
  const childrenByParent = new Map<string, string[]>();
  const assignedChildren = new Set<string>();

  nodes.forEach((node) => {
    const parentId = node?.metadata?.parentNodeId;
    if (!parentId || parentId === node.id || !nodeIds.has(parentId)) return;
    if (!childrenByParent.has(parentId)) childrenByParent.set(parentId, []);
    childrenByParent.get(parentId)!.push(node.id);
    assignedChildren.add(node.id);
  });

  edges.forEach((edge) => {
    const source = String(edge?.source || '');
    const target = String(edge?.target || '');
    if (!source || !target || source === target) return;
    if (!nodeIds.has(source) || !nodeIds.has(target)) return;
    if (assignedChildren.has(target)) return;

    const sourceNode = nodes.find((node) => node.id === source);
    const targetNode = nodes.find((node) => node.id === target);
    const sourceLayer = Number(sourceNode?.metadata?.layer ?? 0);
    const targetLayer = Number(targetNode?.metadata?.layer ?? Number.MAX_SAFE_INTEGER);
    if (source !== rootId && targetLayer <= sourceLayer) return;

    if (!childrenByParent.has(source)) childrenByParent.set(source, []);
    const siblings = childrenByParent.get(source)!;
    if (!siblings.includes(target)) {
      siblings.push(target);
      assignedChildren.add(target);
    }
  });

  return childrenByParent;
}

export function expandGraphWithSourceTypeHubs(
  nodes: any[],
  edges: any[],
  options: {
    rootId: string;
    getNodeLayer: (node: any) => number;
  },
): ExpandedGraphPayload {
  if (!nodes.length) {
    return { nodes: [], edges: [] };
  }

  const nodeById = new Map(nodes.map((node) => [node.id, node]));
  const childrenByParent = buildDirectedChildrenMap(nodes, edges, options.rootId);
  const hubNodes: any[] = [];
  const newEdges: any[] = [];
  const edgesToRemove = new Set<string>();

  childrenByParent.forEach((childIds, parentId) => {
    const groups = new Map<string, any[]>();

    childIds
      .map((childId) => nodeById.get(childId))
      .filter(Boolean)
      .forEach((child) => {
        if (
          child?.metadata?.isAggregateCollection ||
          String(child?.id || '').startsWith('aggregate:')
        ) {
          return;
        }
        const sourceType = resolveNodeSourceType(child);
        if (!shouldCreateHubForSourceType(sourceType)) return;
        if (!groups.has(sourceType)) groups.set(sourceType, []);
        groups.get(sourceType)!.push(child);
      });

    if (groups.size < 2) return;

    groups.forEach((groupChildren, sourceType) => {
      if (!groupChildren.length) return;

      const hubId = buildSourceTypeHubId(parentId, sourceType);
      const parentNode = nodeById.get(parentId);
      const config = SOURCE_TYPE_HUB_CONFIG[sourceType] || {
        ...DEFAULT_HUB_CONFIG,
        label: resolveSourceTypeLabel(sourceType),
      };
      const hubLabel = buildSourceTypeHubLabel(parentNode, sourceType);
      const childLayer = Math.max(
        ...groupChildren.map((child) => options.getNodeLayer(child)),
        1,
      );

      hubNodes.push({
        id: hubId,
        type: config.graphType,
        label: hubLabel,
        role: 'supporting',
        confidence: 0.85,
        evidenceTier: 'tier2',
        sourceType,
        sourceReferences: [{ label: hubLabel, sourceType, url: null }],
        metadata: {
          isSourceTypeHub: true,
          hubSourceType: sourceType,
          parentNodeId: parentId,
          layer: childLayer,
          hubChildIds: groupChildren.map((child) => child.id),
        },
      });

      newEdges.push({
        id: `${parentId}::source_hub::${hubId}`,
        source: parentId,
        target: hubId,
        type: 'linked_to',
        label: hubLabel,
        role: 'supporting',
        confidence: 0.85,
        sourceType,
        evidenceTier: 'tier2',
        sourceReferences: [],
        metadata: { isSourceTypeHubEdge: true },
      });

      groupChildren.forEach((child) => {
        edges.forEach((edge) => {
          const source = String(edge?.source || '');
          const target = String(edge?.target || '');
          if (
            (source === parentId && target === child.id) ||
            (target === parentId && source === child.id)
          ) {
            edgesToRemove.add(edge.id);
          }
        });

        const originalEdge = edges.find(
          (edge) =>
            (edge.source === parentId && edge.target === child.id) ||
            (edge.target === parentId && edge.source === child.id),
        );

        newEdges.push({
          id: `${hubId}::linked_to::${child.id}`,
          source: hubId,
          target: child.id,
          type: originalEdge?.type || 'linked_to',
          label: originalEdge?.label || 'linked',
          role: originalEdge?.role || child.role || 'supporting',
          confidence: child.confidence ?? originalEdge?.confidence ?? 0.55,
          sourceType,
          evidenceTier: child.evidenceTier || originalEdge?.evidenceTier || 'tier3',
          sourceReferences: child.sourceReferences || originalEdge?.sourceReferences || [],
          metadata: {
            ...(originalEdge?.metadata || {}),
            isSourceTypeHubEdge: true,
            originalParentId: parentId,
          },
        });
      });
    });
  });

  const filteredEdges = edges.filter((edge) => !edgesToRemove.has(edge.id));
  return {
    nodes: [...nodes, ...hubNodes],
    edges: [...filteredEdges, ...newEdges],
  };
}

export function orderLayerNodesForSourceHubs(ringNodes: any[]): any[] {
  const hubs = ringNodes.filter((node) => isSourceTypeHubNode(node));
  if (!hubs.length) return ringNodes;

  const hubById = new Map(hubs.map((hub) => [hub.id, hub]));
  const childToHub = new Map<string, string>();
  hubs.forEach((hub) => {
    (hub.metadata?.hubChildIds || []).forEach((childId: string) => {
      childToHub.set(childId, hub.id);
    });
  });

  const assigned = new Set<string>();
  const ordered: any[] = [];

  ringNodes
    .filter((node) => !isSourceTypeHubNode(node) && !childToHub.has(node.id))
    .sort((left, right) => String(left.label || left.id).localeCompare(String(right.label || right.id)))
    .forEach((node) => {
      ordered.push(node);
      assigned.add(node.id);
    });

  hubs
    .sort((left, right) => String(left.label || left.id).localeCompare(String(right.label || right.id)))
    .forEach((hub) => {
      ordered.push(hub);
      assigned.add(hub.id);
      ringNodes
        .filter((node) => childToHub.get(node.id) === hub.id)
        .sort((left, right) =>
          String(left.label || left.id).localeCompare(String(right.label || right.id)),
        )
        .forEach((child) => {
          ordered.push(child);
          assigned.add(child.id);
        });
    });

  ringNodes
    .filter((node) => !assigned.has(node.id))
    .forEach((node) => ordered.push(node));

  return ordered;
}
